import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { AppButton, AppTableImproved, TitleRecord } from "@/shared/Components";
import { useContainerHeight } from "@/shared/hooks";
import { PersonRecord } from "@/common/PersonRecord";
import { useRuntimeImage } from "@/entities/runtime-image";
import { useContactsList, type ContactsListProps } from "@/entities/contacts-list/model";
import { handleFullName, handlePersonFullName } from "@/shared/utils";
import { ImportVcf } from "@/pages/ContactsPage/Components/ImportVcf";
import { StringFilterDropdown } from "@/entities/string-filter-dropdown";
import { PlusIcon } from "@/shared/icons";
import type { Person } from "@/services/api";
import styles from "./styles.module.scss";

export const ContactsListDesktop = ({
  contacts,
  selectedPersonId,
  setSelectedPersonId,
}: ContactsListProps) => {
  const { state, methods } = useContactsList(contacts, setSelectedPersonId);

  const { t } = useTranslation();
  const { containerRef, containerHeight } = useContainerHeight();
  const { getRuntimeImageURL } = useRuntimeImage();

  const rowClassName = (record: Person) => {
    return classNames(styles.row, {
      [styles.selected]: record.id === selectedPersonId,
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner} ref={containerRef}>
        <AppTableImproved
          showSorterTooltip={false}
          className={styles.table}
          dataSource={state.filteredContacts}
          rowKey={(row) => row.id}
          isPaginationVisible={false}
          scroll={{ y: containerHeight - 30 }}
          onRow={(record) => ({
            onClick: () => methods.handleRowClick(record),
          })}
          rowClassName={rowClassName}
          columns={[
            {
              title: t("common.person"),
              dataIndex: ["group"],
              key: "group",
              render: (_, record: ContactsListProps["contacts"][number]) => {
                return (
                  <PersonRecord
                    selected={selectedPersonId === record.id}
                    avatar={getRuntimeImageURL({ fileId: record.avatarFile?.fileId ?? "" })}
                    group={record.group}
                    name={handleFullName(record.firstName, record.lastName)}
                    avatarSize="small"
                  />
                );
              },
              sorter: (a, b) => (handlePersonFullName(a) > handlePersonFullName(b) ? 1 : -1),
              filterDropdown: () => (
                <StringFilterDropdown
                  value={state.nameFilter}
                  handleChange={methods.handleChangeNameFilter}
                  allowClear
                />
              ),
            },
            {
              title: t("common.title"),
              key: "title",
              dataIndex: ["title"],
              render: (_, record) => <TitleRecord title={record.title} />,
              sorter: (a, b) => (a.title && b.title ? (a!.title > b!.title ? 1 : -1) : 1),
              filterDropdown: () => (
                <StringFilterDropdown
                  value={state.titleFilter}
                  handleChange={methods.handleChangeTitleFilter}
                  allowClear
                />
              ),
            },
          ]}
          virtual
        />
      </div>
      <ImportVcf />
      <AppButton
        onClick={methods.handleCreatePerson}
        type="primary"
        className={styles["add-button"]}
      >
        {<PlusIcon className={styles.icon} />}
      </AppButton>
    </div>
  );
};
